import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import duration from 'dayjs/plugin/duration';
import React from 'react';
import {Box} from '@mui/material';
import {Body, designSystemToken, Label} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {SrtItem} from '@contentlab/utils/srtUtils';
import {
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import LEGACY_COLORS from '@/config/legacyColors';
import {DURATION_MINUTES_SECONDS_MILLISECONDS_FORMAT} from '@/config/timeFormats';
import SrtRowEditor from './SrtRowEditor';

dayjs.extend(duration);

function SrtDisplayRow({
  item,
  isEditing,
  isEdited,
  currentIndex,
  setCurrentIndex,
  editRow,
}: {
  item: SrtItem;
  isEditing: boolean;
  isEdited: boolean;
  currentIndex: string | null;
  setCurrentIndex: (index: string | null) => void;
  editRow: (newText: string) => void;
}) {
  const {uiInteraction} = useUiInteractionEvent();

  const formatTime = (time: string) => {
    dayjs.extend(customParseFormat);
    return dayjs(time, 'hh:mm:ss,SSS').format(
      DURATION_MINUTES_SECONDS_MILLISECONDS_FORMAT
    );
  };

  return (
    <Box>
      {isEditing ? (
        <SrtRowEditor
          item={item}
          editRow={(newText, shouldCloseRow) => {
            editRow(newText);
            if (shouldCloseRow) {
              setCurrentIndex(null);
            }
          }}
          isEdited={isEdited}
          restoreOriginal={() => {
            editRow(item.originalText);
          }}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
        />
      ) : (
        <Box
          sx={{
            'cursor': 'pointer',
            'padding': '0px 6px',
            'transition': 'background-color 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: designSystemToken('semantic.bg.tertiary'),
              borderRadius: 2,
            },
          }}
          onClick={() => {
            setCurrentIndex(item.id);
            uiInteraction({
              item_name: 'rephrase_row_pressed',
              interaction_location:
                CONTENT_STUDIO_VIEW_NAMES.REPHRASE_ROW_EDITOR,
              asset_id: String(item.id),
              flow_name: FLOW_NAMES.CONTENT_STUDIO.REPHRASE,
            });
          }}
        >
          <Body
            size="md"
            color={designSystemToken('semantic.fg.inverse-secondary')}
          >{`${formatTime(item.startTime)} → ${formatTime(
            item.endTime
          )}`}</Body>
          <Body
            size="xl"
            color={
              isEdited
                ? LEGACY_COLORS.CONTENT_STUDIO_BRAND
                : designSystemToken('semantic.fg.primary')
            }
          >
            {item.currentText}
          </Body>
        </Box>
      )}
    </Box>
  );
}

export default SrtDisplayRow;
